import { LitElement, html } from 'lit-element';

class BottomTray extends LitElement {
    static get properties() {
        return {
            isOpen: { type: Boolean },
            content: { type: Array },
            activePage: { type: String }
        };
    }

    constructor() {
        super();
        this.isOpen = false;
        this.content = [];
        this.activePage = '';
        this._onDocumentClick = this._onDocumentClick.bind(this);
        this._onWindowResize = this._onWindowResize.bind(this);
        this.lastWidth = window.innerWidth;
    }

    createRenderRoot() {
        return this; // Use light DOM instead of shadow DOM
    }

    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this._onDocumentClick);
        window.addEventListener('resize', this._onWindowResize);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._onDocumentClick);
        window.removeEventListener('resize', this._onWindowResize);
    }

    _onDocumentClick(event) {
        if (this.isOpen && !this.contains(event.target) && !event.target.closest('.hamburger-menu')) {
            this.close();
        }
    }

    _onWindowResize() {
        // Close the tray when window width changes
        const currentWidth = window.innerWidth;
        if (this.isOpen && currentWidth !== this.lastWidth) {
            this.close();
            // Also hide the overlay
            const overlay = document.querySelector('.tray-overlay');
            if (overlay) {
                overlay.classList.remove('open');
            }
        }

        // Hide the tray completely if width is >= 1300px
        if (currentWidth >= 1300 && this.isOpen) {
            this.close();
        }

        this.lastWidth = currentWidth;
    }

    open() {
        this.isOpen = true;
        this.classList.add('open');
        document.body.style.overflow = 'hidden';

        // Make sure the tray is above the overlay in the DOM structure
        const overlay = document.querySelector('.tray-overlay');
        if (overlay && overlay.parentNode) {
            overlay.parentNode.insertBefore(this, overlay.nextSibling);
        }

        // Center the active item after the tray is visible
        setTimeout(() => {
            const activeElement = this.querySelector('.nav-link.active');
            if (activeElement) {
                activeElement.scrollIntoView({ behavior: 'instant', block: 'center' });
            }
        }, 300);
    }

    close() {
        this.isOpen = false;
        this.classList.remove('open');
        document.body.style.overflow = '';
    }

    toggle() {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    generateFilename = (categoryName, pageName, pageNumber) => {
        const categorySlug = categoryName
            .toLowerCase()
            .replace(/^\d+\.\s+/, '') // Remove leading numbers
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-');

        const pageSlug = pageName
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-');

        const filename = `${pageNumber}-${categorySlug}-${pageSlug}.html`;
        pageNumber++;
        return filename;
    };

    getPageNumber = (categoryName, pageName) => {
        let pageNumber = 1;
        let wasFound = false;
        return this.content.reduce((acc, category) => {
            if (!wasFound) {
                category.pages.forEach(page => {
                    if (page.page_name === pageName) {
                        wasFound = true;
                    }
                    if (!wasFound) {
                        pageNumber++;
                    }
                });
            }
            return pageNumber;
        }, pageNumber);
    };

    render() {
        return html`
            <div class="bottom-tray ${this.isOpen ? 'open' : ''}">
                <div class="tray-header">
                    <div class="tray-handle"></div>
                    <button class="close-button" aria-label="Close menu" @click=${this.close} tabindex="0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18" stroke="#54c4b3" stroke-width="1.5" stroke-linecap="round" />
                            <path d="M6 6L18 18" stroke="#54c4b3" stroke-width="1.5" stroke-linecap="round" />
                        </svg>
                    </button>
                </div>
                <div class="tray-content">
                    <nav>
                        ${this.content
                            ? this.content.map(
                                  category => html`
                                      <div class="section">
                                          <h3 class="section-title">${category.category_name}</h3>
                                          <ul class="nav-list">
                                              ${category.pages.map(
                                                  page => html`
                                                      <li class="nav-item">
                                                          <a
                                                              href="${this.generateFilename(
                                                                  category.category_name,
                                                                  page.page_name,
                                                                  this.getPageNumber(
                                                                      category.category_name,
                                                                      page.page_name
                                                                  )
                                                              )}"
                                                              class="nav-link ${this.activePage === page.page_name
                                                                  ? 'active'
                                                                  : ''}"
                                                              tabindex="${this.isOpen ? '0' : '-1'}"
                                                              aria-label="${page.page_name}"
                                                          >
                                                              ${page.page_name}
                                                          </a>
                                                      </li>
                                                  `
                                              )}
                                          </ul>
                                      </div>
                                  `
                              )
                            : html`<div>No content available</div>`}
                    </nav>
                </div>
            </div>
        `;
    }
}

customElements.define('bottom-tray', BottomTray);
